import { useState, useEffect, useCallback } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import Card, { CardHeader, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import LoadingOverlay, { WidgetWrapper } from '@/components/ui/LoadingOverlay';
import { apiClient } from '@/lib/api';
import type { CombinedStats, HealthCheck } from '@/types/api';
import clsx from 'clsx';

// Mock stuck jobs - NOT CONNECTED
const stuckJobs = [
  {
    id: 'job_xyz789',
    title: 'Technical Manual v3',
    stuckAt: 'TTS synthesis',
    duration: '15 minutes',
    lastActivity: '10:15:34',
  },
  {
    id: 'job_abc456',
    title: 'Annual Report 2024',
    stuckAt: 'Image processing',
    duration: '8 minutes',
    lastActivity: '10:22:12',
  },
];

function getStatusColor(status: string) {
  switch (status) {
    case 'healthy':
      return 'bg-success-500';
    case 'degraded':
      return 'bg-warning-500';
    case 'down':
    case 'unhealthy':
      return 'bg-danger-500';
    default:
      return 'bg-slate-400';
  }
}

export default function TTSHealthPage() {
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Real data state
  const [stats, setStats] = useState<CombinedStats | null>(null);
  const [_health, setHealth] = useState<HealthCheck | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [recentActivity, setRecentActivity] = useState<Array<{
    time: string;
    jobId: string;
    event: string;
    message: string;
  }>>([]);

  // Fetch health status from backend
  const fetchHealth = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setIsLoading(true);
      else setIsRefreshing(true);

      // Fetch health check
      try {
        const healthResponse = await apiClient.get<HealthCheck>('public/health');
        setHealth(healthResponse);
      } catch {
        // Health endpoint might not be available
        setHealth({
          status: 'degraded',
          checks: {
            api: 'error',
            tts_worker: 'unknown',
            distribution_worker: 'unknown',
            db: 'unknown',
          },
          timestamp: new Date().toISOString(),
        });
      }

      // Fetch stats for queue info
      const statsResponse = await apiClient.get<CombinedStats>('tts/queue/stats');
      setStats(statsResponse);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch health data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch health data');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Generate simulated activity based on real stats
  useEffect(() => {
    if (stats) {
      const activities = [
        { time: new Date().toLocaleTimeString(), jobId: 'system', event: 'health_check', message: `System status: ${stats.system_status}` },
      ];
      if (stats.queue_stats.processing_jobs > 0) {
        activities.push({ time: new Date().toLocaleTimeString(), jobId: 'queue', event: 'processing', message: `${stats.queue_stats.processing_jobs} jobs processing` });
      }
      if (stats.queue_stats.pending_jobs > 0) {
        activities.push({ time: new Date().toLocaleTimeString(), jobId: 'queue', event: 'pending', message: `${stats.queue_stats.pending_jobs} jobs pending` });
      }
      setRecentActivity(activities);
    }
  }, [stats]);

  // Initial data fetch
  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  // Auto-refresh every 5 seconds when enabled
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchHealth(false);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, fetchHealth]);

  // Build services array from stats and health
  const services: Array<{
    name: string;
    status: string;
    icon: string;
    detail: string;
    isConnected: boolean;
  }> = [
    {
      name: 'TTS Worker',
      status: stats?.system_status === 'healthy' ? 'healthy' : stats?.system_status || 'unknown',
      icon: '⚡',
      detail: `${stats?.queue_stats.active_workers || 0} active`,
      isConnected: !!stats,
    },
    {
      name: 'Job Queue',
      status: stats ? 'healthy' : 'unknown',
      icon: '📋',
      detail: `${stats?.queue_stats.pending_jobs || 0} pending`,
      isConnected: !!stats,
    },
    // These are NOT CONNECTED - mark with red
    {
      name: 'D1 Database',
      status: 'unknown',
      icon: '🗄️',
      detail: '--',
      isConnected: false,
    },
    {
      name: 'KV Storage',
      status: 'unknown',
      icon: '💾',
      detail: '--',
      isConnected: false,
    },
    {
      name: 'R2 Storage',
      status: 'unknown',
      icon: '📦',
      detail: '--',
      isConnected: false,
    },
    {
      name: 'ElevenLabs API',
      status: 'unknown',
      icon: '🔊',
      detail: '--',
      isConnected: false,
    },
  ];

  // Queue stats from real data
  const queueStats = {
    pending: stats?.queue_stats.pending_jobs ?? 0,
    running: stats?.queue_stats.processing_jobs ?? 0,
    completed: stats?.job_metrics.completed_jobs ?? 0,
    failed: stats?.job_metrics.failed_jobs ?? 0,
    total: stats?.job_metrics.total_jobs ?? 0,
    processingRate: '--', // NOT CONNECTED
    avgDuration: stats?.job_metrics.average_processing_time
      ? `${Math.round(stats.job_metrics.average_processing_time / 60000)}m`
      : '--',
    successRate: stats?.job_metrics.success_rate
      ? `${stats.job_metrics.success_rate.toFixed(1)}%`
      : '--',
  };

  const allHealthy = stats?.system_status === 'healthy';

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-slate-800">System Health Dashboard</h1>
          <LoadingOverlay isLoading={isLoading} showSpinner={false} loadingOpacity={50}>
            <Badge variant={allHealthy ? 'success' : error ? 'danger' : 'warning'} size="lg">
              {allHealthy ? '🟢 All Systems Operational' : error ? '🔴 Connection Error' : '🟡 Some Issues Detected'}
            </Badge>
          </LoadingOverlay>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500">Auto-refresh:</span>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={clsx(
              'px-3 py-1 rounded-lg text-sm font-medium transition-colors',
              autoRefresh ? 'bg-success-100 text-success-700' : 'bg-slate-100 text-slate-600'
            )}
          >
            {autoRefresh ? 'ON' : 'OFF'}
          </button>
          <Button
            variant="outline"
            leftIcon={<ArrowPathIcon className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />}
            onClick={() => fetchHealth(false)}
            disabled={isRefreshing}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Service Status Grid */}
      <LoadingOverlay isLoading={isLoading} loadingOpacity={40} showSpinner={false}>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {services.map((service) => (
            <div
              key={service.name}
              className={clsx(
                'transition-opacity',
                !service.isConnected && 'opacity-20'
              )}
            >
              <Card>
                <CardBody className="text-center">
                  <div className="text-3xl mb-2">{service.icon}</div>
                  <p className={clsx(
                    'font-medium mb-1',
                    service.isConnected ? 'text-slate-800' : 'text-red-500'
                  )}>
                    {service.name}
                  </p>
                  <div className="flex items-center justify-center gap-2">
                    <div className={clsx('w-2 h-2 rounded-full', getStatusColor(service.status))} />
                    <span className={clsx(
                      'text-sm capitalize',
                      service.isConnected ? 'text-slate-500' : 'text-red-500'
                    )}>
                      {service.isConnected ? service.status : 'Not Connected'}
                    </span>
                  </div>
                  <p className={clsx(
                    'text-xs mt-1',
                    service.isConnected ? 'text-slate-400' : 'text-red-400'
                  )}>
                    {service.detail}
                  </p>
                </CardBody>
              </Card>
            </div>
          ))}
        </div>
      </LoadingOverlay>

      {/* Queue Metrics - Connected to Real Data */}
      <LoadingOverlay isLoading={isLoading} loadingOpacity={40}>
        <Card>
          <CardHeader title="Queue Metrics" />
          <CardBody>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
              <div className="text-center p-4 bg-warning-50 rounded-xl">
                <p className="text-3xl font-bold text-warning-600">{queueStats.pending}</p>
                <p className="text-sm text-slate-600">Pending</p>
              </div>
              <div className="text-center p-4 bg-primary-50 rounded-xl">
                <p className="text-3xl font-bold text-primary-600">{queueStats.running}</p>
                <p className="text-sm text-slate-600">Running</p>
              </div>
              <div className="text-center p-4 bg-success-50 rounded-xl">
                <p className="text-3xl font-bold text-success-600">{queueStats.completed}</p>
                <p className="text-sm text-slate-600">Complete</p>
              </div>
              <div className="text-center p-4 bg-danger-50 rounded-xl">
                <p className="text-3xl font-bold text-danger-600">{queueStats.failed}</p>
                <p className="text-sm text-slate-600">Failed</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-xl">
                <p className="text-3xl font-bold text-slate-700">{queueStats.total}</p>
                <p className="text-sm text-slate-600">Total</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-3 gap-4 text-center">
              <div>
                <p className={clsx(
                  'text-lg font-semibold',
                  queueStats.processingRate === '--' ? 'text-red-500 opacity-50' : 'text-slate-800'
                )}>
                  {queueStats.processingRate}
                </p>
                <p className="text-sm text-slate-500">Processing Rate</p>
              </div>
              <div>
                <p className="text-lg font-semibold text-slate-800">{queueStats.avgDuration}</p>
                <p className="text-sm text-slate-500">Avg Duration</p>
              </div>
              <div>
                <p className="text-lg font-semibold text-success-600">{queueStats.successRate}</p>
                <p className="text-sm text-slate-500">Success Rate</p>
              </div>
            </div>
          </CardBody>
        </Card>
      </LoadingOverlay>

      {/* Stuck Job Alerts - NOT CONNECTED (20% opacity) */}
      <WidgetWrapper isConnected={false}>
        {stuckJobs.length > 0 && (
          <Card className="border-warning-200 bg-warning-50">
            <CardHeader title="⚠️ Stuck Job Alerts" />
            <CardBody>
              <div className="space-y-4">
                {stuckJobs.map((job) => (
                  <div
                    key={job.id}
                    className="flex items-center justify-between p-4 bg-white rounded-xl border border-warning-200"
                  >
                    <div>
                      <p className="font-medium text-slate-800">{job.title}</p>
                      <p className="text-sm text-slate-500">
                        {job.id} • Stuck at {job.stuckAt} for {job.duration}
                      </p>
                      <p className="text-xs text-slate-400">Last activity: {job.lastActivity}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm">View Details</Button>
                      <Button variant="secondary" size="sm">Retry</Button>
                      <Button variant="danger" size="sm">Cancel</Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        )}
      </WidgetWrapper>

      {/* Recent Activity - Partially Connected */}
      <Card>
        <CardHeader title="Recent Activity (Live)" />
        <CardBody>
          {error ? (
            <div className="text-center py-8">
              <p className="text-red-500">{error}</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => fetchHealth()}
              >
                Retry Connection
              </Button>
            </div>
          ) : recentActivity.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No recent activity
            </div>
          ) : (
            <div className="space-y-2">
              {recentActivity.map((activity, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 py-2 border-b border-slate-50 last:border-0"
                >
                  <span className="font-mono text-sm text-slate-400 w-20">{activity.time}</span>
                  <span className="font-mono text-xs text-slate-500 w-24">{activity.jobId}</span>
                  <span className="text-slate-700">{activity.message}</span>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
